<script>
	import _ from 'underscore';
	import accountUtilities from 'utilities/accountUtilities';
	import countries from 'data/countries';
	import discoveryService from 'services/discoveryService';
	import metadata from 'core/metadata';
	import utilities from 'utilities';
	import donationUtilities from 'utilities/donationUtilities';
	import GradeSelector from 'components/grade-selector';
	import ItemSelector from 'components/item-selector';
	import Message from 'components/message';
	import ToggleButtons from 'components/toggle-buttons';

	const SEPARATOR = {
		text: '-----------------',
		value: null,
		disabled: true
	};

	const CANT_FIND_MY_SCHOOL = {
		text: "I can't find my school.",
		value: 'NOT_FOUND'
	};

	const BLANK_USER_FORM = {
		grades: [],
		state: '',
		country: 'US',
		role: '',
		k12: true,
		schoolId: '',
		digital: false,
		print: false,
		project: false,
		curriculum: [],
		ssoSystems: []
	};

	export default {
		props: ['original-account', 'submit-button-text', 'success-handler'],

		components: {
			'grade-selector': GradeSelector,
			'item-selector': ItemSelector,
			'toggle-buttons': ToggleButtons,
			message: Message
		},

		data() {
			return {
				states: countries.US.children.map(state => ({
					value: state,
					text: state
				})),
				schools: [],
				roles: [...metadata.roles],
				userSetupForm: null,
				zipCodeError: []
			};
		},
		computed: {
			gradeSelections() {
				return this.getMappedSelections('grades', 'gradeSelections');
			},
			curriculumSelections() {
				return this.getMappedSelections('curriculum', 'curriculum');
			},
			ssoSelections() {
				return this.getMappedSelections('ssoSystems', 'ssoSystems');
			},
			isFormValid() {
				return (this.$el?.checkValidity?.() ?? true) && this.userSetupForm?.role;
			},
			k12Text() {
				if (this.userSetupForm?.role?.toString() === '385') {
					return 'My child is in a PreK-12 School/District';
				}
				return "I'm in a PreK-12 School/District";
			},
			gradeSelectionText() {
				if (this.userSetupForm?.role?.toString() === '385') {
					return 'Grade Level(s) of Your Children';
				}
				return 'Grade Level(s) You Teach';
			},
			placeholder() {
				if (utilities.regexp.zipcode.test(this.userSetupForm.schoolZip)) {
					return 'Please choose a school';
				}

				return 'Enter ZIP code above to choose a school';
			}
		},
		methods: {
			async submitHandler() {
				const {
					k12,
					schoolId,
					schoolName,
					city = '',
					state = '',
					country = '',
					role,
					grades,
					print,
					digital,
					project,
					ssoSystems,
					curriculum
				} = this.userSetupForm;

				let params = {
					k12,
					grades,
					print,
					digital,
					project,
					curriculum,
					ssoSystems
				};

				if (k12) {
					if (schoolId === 'NOT_FOUND') {
						params.schoolName = schoolName;
						params.schoolId = '';
					} else if (schoolId) {
						params.schoolId = schoolId;

						const schoolText = this.schools.find(school => school.value === schoolId)?.text;

						if (schoolText) {
							params.schoolName = schoolText;
						}
					}
				} else {
					params = {
						...params,
						city,
						state,
						country
					};
				}

				if (role !== '') {
					if (!isNaN(Number(role))) {
						params.role = Number(role);
					}
				}

				await accountUtilities.update(params);
				this.successHandler?.();
			},
			onItemSelection(key, item) {
				const itemIndex = this.userSetupForm[key].indexOf(item.id);
				if (itemIndex === -1) {
					this.userSetupForm[key].push(item.id);
				} else {
					this.userSetupForm[key].splice(itemIndex, 1);
				}
			},
			getInitialSchoolsList() {
				if (this.originalAccount?.schoolId) {
					return [
						{
							text: this.originalAccount.schoolName,
							value: this.originalAccount.schoolId
						},
						SEPARATOR,
						CANT_FIND_MY_SCHOOL
					];
				}
				return [];
			},
			getMappedSelections(formKey, metadataKey) {
				const selectedItems = new Set(this.userSetupForm[formKey]);

				return metadata[metadataKey].map(item => ({
					...item,
					selected: selectedItems.has(item.id)
				}));
			}
		},

		watch: {
			originalAccount: {
				handler(originalAccount) {
					// Assign empty form defaults for required fields (country, role, grades, etc.)
					let { role } = originalAccount ?? {};
					role = `_${role}` in metadata.rolesById ? role : '';

					const form = {
						...BLANK_USER_FORM,
						...originalAccount,
						role
					};

					this.userSetupForm = form;
					this.schools = this.getInitialSchoolsList();

					this.userSetupForm.schoolId = originalAccount?.schoolId ? originalAccount.schoolId : originalAccount?.schoolName ? 'NOT_FOUND' : null;
				},
				immediate: true
			},

			'userSetupForm.schoolZip': {
				handler: _.debounce(async function (zip) {
					if (utilities.regexp.zipcode.test(zip)) {
						const { hits } = await discoveryService.searchSchool({
							zip
						});

						if (hits) {
							this.userSetupForm.schoolId = '';

							const schools = hits
								.map(hit => ({
									value: hit.id,
									text: hit.name
								}))
								.sort((a, b) => a.text.localeCompare(b.text, { sensitivity: 'base' }));

							this.schools = [...schools, SEPARATOR, CANT_FIND_MY_SCHOOL];
						}

						this.zipCodeError = [];
					} else {
						this.schools = this.getInitialSchoolsList();

						if (zip) {
							this.zipCodeError = zip ? ['Invalid zipcode.'] : [];
						}
					}
				}, 500),

				immediate: true
			},
			'userSetupForm.role': {
				handler(role) {
					if (donationUtilities.isRoleThatShowsModal(role)) {
						donationUtilities.showDonorboxModalOnce({
							role
						});
					}
				}
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';

	.user-setup-form {
		max-width: 50rem;
		margin: 0 auto;

		:invalid,
		[aria-invalid='true'] {
			border-color: $c-red;
		}

		h2 {
			color: $c-blue-dark;
			margin-top: 4rem;
		}
	}
</style>
<template>
	<form
		class="user-setup-form form"
		@submit.prevent="submitHandler"
		novalidate>
		<fieldset
			class="margin-bottom-smedium input-checkbox-grid"
			aria-label="Choose your school type">
			<div class="input-checkbox-grid-item -one-half">
				<input
					type="radio"
					name="k12"
					v-model="userSetupForm.k12"
					:value="true"
					id="input-k12-1" />
				<label for="input-k12-1">
					<span>{{ k12Text }}</span>
				</label>
			</div>
			<div class="input-checkbox-grid-item -one-half">
				<input
					type="radio"
					name="k12"
					v-model="userSetupForm.k12"
					:value="false"
					id="input-k12-2" />
				<label for="input-k12-2"><span>Other</span></label>
			</div>
		</fieldset>
		<div
			v-if="userSetupForm.k12"
			class="k12-form">
			<div class="margin-bottom-smedium">
				<label for="zip-code-input">School Zip Code</label>
				<input
					id="zip-code-input"
					class="input"
					type="text"
					pattern="(^\d{5}$)|(^\d{5}-\d{4}$)"
					:aria-invalid="!!zipCodeError?.length"
					aria-describedby="zip-code-error-message"
					v-model="userSetupForm.schoolZip" />
				<message
					v-if="!!zipCodeError?.length"
					type="error">
					<ul
						id="zip-code-error-message"
						class="list-unstyled">
						<li
							v-for="e in zipCodeError"
							:key="e"
							v-html="e"></li>
					</ul>
				</message>
			</div>
			<div class="margin-bottom-smedium">
				<label for="school-select">School</label>
				<div class="input-select">
					<select
						id="school-select"
						v-model="userSetupForm.schoolId">
						<option
							disabled
							value="">
							{{ placeholder }}
						</option>
						<option
							v-for="school in schools"
							:disabled="school.disabled"
							:key="school.value"
							:value="school.value">
							{{ school.text }}
						</option>
					</select>
				</div>
			</div>
			<div
				v-if="userSetupForm.schoolId === 'NOT_FOUND'"
				class="margin-bottom-smedium">
				<label for="school-not-found-input">Enter Your School's Name</label>
				<input
					id="school-not-found-input"
					class="input"
					type="text"
					maxlength="64"
					v-model="userSetupForm.schoolName" />
			</div>
		</div>
		<div v-else>
			<div class="margin-bottom-smedium">
				<label for="city-input">City</label>
				<input
					id="city-input"
					class="input"
					type="text"
					maxlength="64"
					v-model="userSetupForm.city" />
			</div>
			<div class="margin-bottom-smedium">
				<label for="state-select">State</label>
				<div class="input-select">
					<select
						id="state-select"
						v-model="userSetupForm.state">
						<option
							disabled
							value="">
							State
						</option>
						<option
							v-for="state in states"
							:key="state.value"
							:value="state.value">
							{{ state.text }}
						</option>
					</select>
				</div>
			</div>
		</div>
		<div class="margin-bottom-smedium">
			<label for="role-select">Role</label>
			<div class="input-select">
				<select
					id="role-select"
					v-model="userSetupForm.role">
					<option
						disabled
						value="">
						Please choose a Role
					</option>
					<option
						v-for="role in roles"
						:key="role.value"
						:value="role.value">
						{{ role.text }}
					</option>
				</select>
			</div>
		</div>
		<grade-selector
			class="margin-bottom-smedium"
			:legend="gradeSelectionText"
			:grades="gradeSelections"
			:on-grade-click="grade => onItemSelection('grades', grade)"
			prek-view>
		</grade-selector>
		<template v-if="userSetupForm.k12">
			<h2>Additional Details</h2>
			<item-selector
				class="margin-bottom-smedium curriculum-selector"
				legend="Please share your Curriculum/Standards"
				:items="curriculumSelections"
				:on-item-click="item => onItemSelection('curriculum', item)">
			</item-selector>
			<item-selector
				class="margin-bottom-smedium sso-systems-selector"
				legend="Please share your SSO system(s)"
				:items="ssoSelections"
				:on-item-click="item => onItemSelection('ssoSystems', item)">
			</item-selector>
		</template>
		<button
			class="submit button -full-width margin-top-bottom-large"
			:class="{
				disabled: !isFormValid
			}"
			type="submit"
			:disabled="!isFormValid">
			{{ submitButtonText || 'Get Started' }}
		</button>
	</form>
</template>
